import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import type { User, SubscriptionTierId } from '../types';

// Storage key
const AUTH_STORAGE_KEY = 'brickmod-auth';

// State interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Action types
type AuthAction =
  | { type: 'LOGIN'; payload: User }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'UPDATE_TIER'; payload: SubscriptionTierId }
  | { type: 'PAUSE_SUBSCRIPTION'; payload: string } // pausedUntil date
  | { type: 'RESUME_SUBSCRIPTION' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'HYDRATE'; payload: AuthState };

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };

    case 'UPDATE_USER':
      if (!state.user) return state;
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    case 'UPDATE_TIER':
      if (!state.user) return state;
      return {
        ...state,
        user: { ...state.user, tier: action.payload },
      };

    case 'PAUSE_SUBSCRIPTION':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          isPaused: true,
          pausedUntil: action.payload,
        },
      };

    case 'RESUME_SUBSCRIPTION':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          isPaused: false,
          pausedUntil: undefined,
        },
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'HYDRATE':
      return {
        ...action.payload,
        isLoading: false,
      };

    default:
      return state;
  }
}

// Context value interface
interface AuthContextValue extends AuthState {
  login: (user: User) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  updateTier: (tier: SubscriptionTierId) => void;
  pauseSubscription: (pausedUntil: string) => void;
  resumeSubscription: () => void;
}

// Create context
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Storage helper functions
function saveAuthToStorage(state: AuthState): void {
  if (typeof window === 'undefined') return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isLoading: _, ...stateToSave } = state;
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    console.warn('Failed to save auth state to localStorage:', error);
  }
}

interface StoredAuthState {
  user: User | null;
  isAuthenticated: boolean;
}

function loadAuthFromStorage(): AuthState | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as StoredAuthState;
      return {
        user: parsed.user,
        isAuthenticated: parsed.isAuthenticated,
        isLoading: false,
      };
    }
  } catch (error) {
    console.warn('Failed to load auth state from localStorage:', error);
  }
  return null;
}

function clearAuthFromStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear auth state from localStorage:', error);
  }
}

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const storedState = loadAuthFromStorage();
    if (storedState && storedState.isAuthenticated && storedState.user) {
      dispatch({ type: 'HYDRATE', payload: storedState });
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Persist to localStorage when state changes
  useEffect(() => {
    if (!state.isLoading) {
      if (state.isAuthenticated && state.user) {
        saveAuthToStorage(state);
      } else {
        clearAuthFromStorage();
      }
    }
  }, [state]);

  const login = useCallback((user: User) => {
    dispatch({ type: 'LOGIN', payload: user });
  }, []);

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' });
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', payload: updates });
  }, []);

  const updateTier = useCallback((tier: SubscriptionTierId) => {
    dispatch({ type: 'UPDATE_TIER', payload: tier });
  }, []);

  const pauseSubscription = useCallback((pausedUntil: string) => {
    dispatch({ type: 'PAUSE_SUBSCRIPTION', payload: pausedUntil });
  }, []);

  const resumeSubscription = useCallback(() => {
    dispatch({ type: 'RESUME_SUBSCRIPTION' });
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    updateUser,
    updateTier,
    pauseSubscription,
    resumeSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
